import { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { useSessionPolicy, useUpdateSessionPolicy } from '../../hooks/useSessionPolicy';
import { Button, Field, FormSection, Input } from '../../ui';
import type {
  SecurityMode,
  SessionPolicySettings as SessionPolicySettingsType,
  SessionPolicyUpdatePayload,
} from '../../types/sessionPolicy';
import './SessionPolicySettings.css';

interface SessionPolicySettingsProps {
  projectId: string;
  canManage: boolean;
}

const SECURITY_MODES: { value: SecurityMode; label: string; hint: string }[] = [
  { value: 'strict', label: 'Strict', hint: 'Short tokens, low cap, frequent step-up' },
  { value: 'standard', label: 'Standard', hint: 'Balanced defaults for most apps' },
  { value: 'relaxed', label: 'Relaxed', hint: 'Long tokens, no cap, infrequent step-up' },
];

// Each control accepts a numeric override or "" to mean "inherit preset".
// Stored as string in local state so the input can be cleared without
// triggering "0 = disabled" semantics by accident.
type Override = '' | number;

export function SessionPolicySettings({ projectId, canManage }: SessionPolicySettingsProps) {
  const { data: policy, isLoading, isError, error } = useSessionPolicy(projectId);
  const updatePolicy = useUpdateSessionPolicy();

  const [securityMode, setSecurityMode] = useState<SecurityMode>('standard');
  const [accessTtl, setAccessTtl] = useState<Override>('');
  const [refreshTtl, setRefreshTtl] = useState<Override>('');
  const [idleTimeout, setIdleTimeout] = useState<Override>('');
  const [maxConcurrent, setMaxConcurrent] = useState<Override>('');
  const [stepUpWindow, setStepUpWindow] = useState<Override>('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!policy) return;
    setSecurityMode(policy.security_mode);
    setAccessTtl(policy.access_token_ttl_minutes ?? '');
    setRefreshTtl(policy.refresh_token_ttl_days ?? '');
    setIdleTimeout(policy.idle_timeout_minutes ?? '');
    setMaxConcurrent(policy.max_concurrent_sessions ?? '');
    setStepUpWindow(policy.step_up_reauth_window_minutes ?? '');
  }, [policy]);

  const isDirty = useMemo(() => {
    if (!policy) return false;
    return (
      securityMode !== policy.security_mode ||
      normalize(accessTtl) !== policy.access_token_ttl_minutes ||
      normalize(refreshTtl) !== policy.refresh_token_ttl_days ||
      normalize(idleTimeout) !== policy.idle_timeout_minutes ||
      normalize(maxConcurrent) !== policy.max_concurrent_sessions ||
      normalize(stepUpWindow) !== policy.step_up_reauth_window_minutes
    );
  }, [policy, securityMode, accessTtl, refreshTtl, idleTimeout, maxConcurrent, stepUpWindow]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage || !policy) return;

    const payload: SessionPolicyUpdatePayload = {
      security_mode: securityMode,
      access_token_ttl_minutes: normalize(accessTtl),
      refresh_token_ttl_days: normalize(refreshTtl),
      idle_timeout_minutes: normalize(idleTimeout),
      max_concurrent_sessions: normalize(maxConcurrent),
      step_up_reauth_window_minutes: normalize(stepUpWindow),
    };

    try {
      await updatePolicy.mutateAsync({ projectId, data: payload });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      /* surfaced via mutation error below */
    }
  };

  if (isLoading) {
    return <p className="session-policy-settings__loading">Loading session policy…</p>;
  }
  if (isError || !policy) {
    return (
      <p className="session-policy-settings__error">
        {error?.message || 'Failed to load session policy.'}
      </p>
    );
  }

  return (
    <FormSection
      title={
        <span className="session-policy-settings__title">
          <Clock size={18} aria-hidden="true" /> End-User Session Policy
        </span>
      }
      description="Controls how long end-user sessions stay valid, how many simultaneous sessions a single end user can hold, and when fresh MFA is required for sensitive actions. Leave any field blank to inherit the security-mode preset."
    >
      <form onSubmit={handleSave} className="session-policy-settings__form">
        <Field
          label="Security mode preset"
          htmlFor="securityMode"
          hint="Each preset sets sensible defaults for all fields below; explicit values override the preset."
        >
          <select
            id="securityMode"
            className="session-policy-settings__select"
            value={securityMode}
            disabled={!canManage}
            onChange={e => setSecurityMode(e.target.value as SecurityMode)}
          >
            {SECURITY_MODES.map(mode => (
              <option key={mode.value} value={mode.value}>
                {mode.label} — {mode.hint}
              </option>
            ))}
          </select>
        </Field>

        <div className="session-policy-settings__grid">
          <OverrideField
            id="accessTtl"
            label="Access token TTL (minutes)"
            resolved={policy.resolved_access_token_ttl_minutes}
            value={accessTtl}
            onChange={setAccessTtl}
            disabled={!canManage}
            hint="5–1440. How long a single access token stays valid."
          />

          <OverrideField
            id="refreshTtl"
            label="Refresh token TTL (days)"
            resolved={policy.resolved_refresh_token_ttl_days}
            value={refreshTtl}
            onChange={setRefreshTtl}
            disabled={!canManage}
            hint="1–180. Absolute lifetime of an end-user session."
          />

          <OverrideField
            id="idleTimeout"
            label="Idle timeout (minutes)"
            resolved={policy.resolved_idle_timeout_minutes}
            value={idleTimeout}
            onChange={setIdleTimeout}
            disabled={!canManage}
            hint="0 disables. Revoke sessions whose last activity is older than this."
          />

          <OverrideField
            id="maxConcurrent"
            label="Max concurrent sessions"
            resolved={policy.resolved_max_concurrent_sessions}
            value={maxConcurrent}
            onChange={setMaxConcurrent}
            disabled={!canManage}
            hint="0 means unlimited. New logins displace the oldest sessions."
          />

          <OverrideField
            id="stepUpWindow"
            label="Step-up reauth window (minutes)"
            resolved={policy.resolved_step_up_reauth_window_minutes}
            value={stepUpWindow}
            onChange={setStepUpWindow}
            disabled={!canManage}
            hint="0 disables. Sensitive ops (e.g. disabling MFA) require fresh MFA within this window."
          />
        </div>

        {updatePolicy.isError && (
          <p className="session-policy-settings__error">
            {updatePolicy.error?.message || 'Failed to save session policy'}
          </p>
        )}
        {saved && <p className="session-policy-settings__success">Session policy saved.</p>}

        {canManage && (
          <div className="session-policy-settings__actions">
            <Button
              type="submit"
              loading={updatePolicy.isPending}
              disabled={!isDirty || updatePolicy.isPending}
            >
              {updatePolicy.isPending ? 'Saving…' : 'Save Session Policy'}
            </Button>
          </div>
        )}
      </form>
    </FormSection>
  );
}

interface OverrideFieldProps {
  id: string;
  label: string;
  resolved?: number;
  value: Override;
  onChange: (v: Override) => void;
  disabled: boolean;
  hint: string;
}

function OverrideField({
  id,
  label,
  resolved,
  value,
  onChange,
  disabled,
  hint,
}: OverrideFieldProps) {
  return (
    <Field
      label={label}
      htmlFor={id}
      hint={resolved !== undefined ? `${hint} Currently effective: ${resolved}.` : hint}
    >
      <Input
        id={id}
        type="number"
        min={0}
        value={value === '' ? '' : String(value)}
        placeholder="Inherit preset"
        disabled={disabled}
        onChange={e => {
          const raw = e.target.value;
          if (raw === '') {
            onChange('');
          } else {
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : '');
          }
        }}
      />
    </Field>
  );
}

function normalize(value: Override): number | null {
  return value === '' ? null : Number(value);
}

export type { SessionPolicySettingsType };
